import { useCallback, useState } from 'react';

import { ClientRoles } from '@explorer/forms/types';
import { getRuntimeEnv } from '@explorer/helpers';
import { useQuerySnackbar } from '@explorer/hooks';
import { ControlClientInfoResponse } from '@explorer/open-api-client';
import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';

import { ROLE_HIERARCHY } from './constants';
import { FormValues } from './formSchema';
import { useMutationDeletePrePurchase } from './queries/useMutationDeletePrePurchase';
import { useMutationUpdateClient } from './queries/useMutationUpdateClient';
import {
  MDRAssociationAction,
  useMutationUpdateMDRAssociation,
} from './queries/useMutationUpdateMDRAssociation';
import { Modules } from './types';

interface UseModifyClientSubmitProps {
  activeClient?: ControlClientInfoResponse;
  clientId?: string;
  modules: Modules;
}

export const useModifyClientSubmit = ({
  activeClient,
  clientId,
  modules,
}: UseModifyClientSubmitProps) => {
  const { snackError, snackSuccess } = useQuerySnackbar();
  const { formatMessage } = useIntl();

  const { mutateAsync: mutateClientAsync } = useMutationUpdateClient();
  const { mutateAsync: deletePrepurchaseAsync } =
    useMutationDeletePrePurchase();
  const { mutateAsync: mutateMDRAssociationAsync } =
    useMutationUpdateMDRAssociation();

  const [open, setOpen] = useState(false);

  const onSubmit = useCallback(
    async (
      values: FormValues,
      formikHelpers: FormikHelpers<FormValues>,
      confirmed: boolean,
    ) => {
      const { setSubmitting } = formikHelpers;
      const componentActivated = !!Object.entries(
        values.control_components,
      ).find(([, overridden]) => {
        return !!overridden;
      });

      const control_components = {
        ...values.control_components,
        MTC: values.control_components.MTC
          ? { component_details_json: values.mtc_details }
          : {},
      };

      const hasPrepurchaseComponents = Object.entries(
        modules.prepurchasedModules,
      ).find(([, prepurchaseInfo]) => {
        return !!prepurchaseInfo;
      });

      // If flipped to active, and has prepurchase components, we need to show a confirmation dialog
      if (componentActivated && hasPrepurchaseComponents && !confirmed) {
        setOpen(true);
        return;
      }

      try {
        await mutateClientAsync({
          clientId,
          role: values?.role ?? ClientRoles.PORTAL_FREEMIUM,
          count_max_lite: values?.count_max_lite ?? 0,
          msp: values?.control_components?.MTC ?? false,
          count_max_extended: values?.count_max_extended ?? 0,
          control_components,
        });

        if (componentActivated && hasPrepurchaseComponents) {
          try {
            await deletePrepurchaseAsync({
              clientId,
            });
          } catch (e) {
            snackError('Failed to clear prepurchased modules', e);
          }
        }

        const isMDRActive = !!activeClient?.client?.active_components?.MDR;

        if (isMDRActive !== values.control_components.MDR) {
          await mutateMDRAssociationAsync({
            clientId,
            action: values.control_components.MDR
              ? MDRAssociationAction.Associate
              : MDRAssociationAction.Disassociate,
            mspClientId: getRuntimeEnv('MDR_CLIENT_ID'),
          });
        }

        // Determine if it's an upgrade or downgrade
        const currentRole =
          ROLE_HIERARCHY[activeClient?.client?.role as ClientRoles] || 0;
        const newRole =
          ROLE_HIERARCHY[values?.role ?? ClientRoles.PORTAL_FREEMIUM] || 0;

        const messageId =
          newRole > currentRole
            ? 'forms-presets.success-upgrade-client'
            : 'forms-presets.success-downgrade-client';

        snackSuccess(formatMessage({ id: messageId }, { client: clientId }));

        setSubmitting(false);
      } catch (e) {
        snackError('Modify Client Error', e);
      }
    },
    [
      clientId,
      deletePrepurchaseAsync,
      formatMessage,
      modules.prepurchasedModules,
      mutateClientAsync,
      snackError,
      snackSuccess,
      activeClient?.client?.active_components,
      mutateMDRAssociationAsync,
      activeClient?.client?.role,
    ],
  );

  return {
    onSubmit,
    open,
    setOpen,
  };
};
